import {
  EnumeratedMediaDevices,
  MediaDevicesPermissions,
  PermissionStatus,
} from 'features/user-media/types';
import { call, put, select } from 'redux-saga/effects';
import {
  devicesAutoChanged,
  mediaDevicesUpdated,
  selectAllMediaDevicePermissions,
} from 'features/user-media/userMediaSlice';
import {
  DefaultDevice,
  getDefaultMediaDevice,
  getDevicePermissions,
} from 'features/user-media/utils';
import { PublishingFeedStatus, RTCClient } from 'utils/webrtc';
import { AutoChangedDevices } from 'features/user-media/utils/findDeviceDifferences';
import * as Sentry from '@sentry/react';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { logger } from 'utils/logger';

export function* handleAddedDevicesSaga(
  updatedDevices: EnumeratedMediaDevices,
  addedDevices: AutoChangedDevices
) {
  const currentPermissions: MediaDevicesPermissions = yield select(selectAllMediaDevicePermissions);

  yield put(
    mediaDevicesUpdated(updatedDevices, {
      updatePermissions: true,
    })
  );

  if (currentPermissions.audioinput !== 'granted' && addedDevices.audioinput) {
    try {
      const feedConnected = RTCClient.publishingFeed.status === PublishingFeedStatus.connected;

      if (feedConnected) {
        const canBroadcastAudio: boolean = yield call(
          hasPermissionsSaga,
          PermissionTypes.broadcastAudio
        );

        const devicePermissions: PermissionStatus = yield call(
          getDevicePermissions,
          updatedDevices,
          'audioinput'
        );

        if (canBroadcastAudio && devicePermissions === 'granted') {
          yield call(RTCClient.publishingFeed.updateStreamingPermissions);

          const deviceId: DefaultDevice = yield call(
            getDefaultMediaDevice,
            updatedDevices,
            'audioinput'
          );

          if (!deviceId) {
            logger.remote().info('No audio device found', {
              deviceLabel: addedDevices.audioinput?.label,
            });
          } else {
            logger
              .remote()
              .debug(
                'Updated active audio device in Redux (will be used on next broadcast start)',
                {
                  deviceId,
                  deviceLabel: addedDevices.audioinput?.label,
                }
              );

            yield put(
              devicesAutoChanged({
                kind: 'audioinput',
                id: deviceId,
              })
            );
          }
        } else if (!canBroadcastAudio) {
          logger.remote().warn('Audio device blocked - no broadcast permission', {
            deviceLabel: addedDevices.audioinput?.label,
          });
        }
      }
    } catch (error) {
      logger.remote().error('Error activating added audio device', error);
      Sentry.captureException(error);
      yield call(RTCClient.publishingFeed.blockAudioPublishing);
    }
  }

  if (currentPermissions.videoinput !== 'granted' && addedDevices.videoinput) {
    try {
      const feedConnected = RTCClient.publishingFeed.status === PublishingFeedStatus.connected;

      if (feedConnected) {
        const canBroadcastVideo: boolean = yield call(
          hasPermissionsSaga,
          PermissionTypes.broadcastVideo
        );

        const devicePermissions: PermissionStatus = yield call(
          getDevicePermissions,
          updatedDevices,
          'videoinput'
        );

        if (canBroadcastVideo && devicePermissions === 'granted') {
          yield call(RTCClient.publishingFeed.updateStreamingPermissions);

          const deviceId: DefaultDevice = yield call(
            getDefaultMediaDevice,
            updatedDevices,
            'videoinput'
          );

          if (!deviceId) {
            logger.remote().info('No video device found', {
              deviceLabel: addedDevices.videoinput?.label,
            });
          } else {
            logger
              .remote()
              .debug(
                'Updated active video device in Redux (will be used on next broadcast start)',
                {
                  deviceId,
                  deviceLabel: addedDevices.videoinput?.label,
                }
              );

            yield put(
              devicesAutoChanged({
                kind: 'videoinput',
                id: deviceId,
              })
            );
          }
        } else if (!canBroadcastVideo) {
          logger.remote().warn('Video device blocked - no broadcast permission', {
            deviceLabel: addedDevices.videoinput?.label,
          });
        }
      }
    } catch (error) {
      logger.remote().error('Error activating added video device', error);
      Sentry.captureException(error);
      yield call(RTCClient.publishingFeed.blockVideoPublishing);
    }
  }

  if (addedDevices.audiooutput) {
    const deviceId: DefaultDevice = yield call(
      getDefaultMediaDevice,
      updatedDevices,
      'audiooutput'
    );

    if (!deviceId) {
      logger.remote().info('No audio output device found', {
        deviceLabel: addedDevices.audiooutput?.label,
      });
    } else {
      yield put(
        devicesAutoChanged({
          kind: 'audiooutput',
          id: deviceId,
        })
      );
    }
  }
}
